import type { CellulaTheme } from '@cellula/react';
import { primitiveColorTokens, tokens } from './tokens';
import { tokensData } from './tokens_data';

// Should use @visiba/std
// https://github.com/VisibaCare/visiba-cortex/pull/133
// https://github.com/VisibaCare/visiba-cortex/pull/215
export function rem(px: number): string {
	return `${px / 16}rem`;
}

export const theme: CellulaTheme = {
	tokens: {
		...tokensData,
	},
	components: {
		common: {
			focusColor: tokens.color.focus,
		},
		aspectRatio: {},
		accordion: {
			headerFont: tokens.text.label.largeSemiBold,
			bodyFont: tokens.text.body.regular,
		},
		actionCard: {
			baseBorderRadius: tokens.borderRadius.medium,
			baseBorderColor: tokens.color.border.muted,
			baseBoxShadow: tokens.elevation.skim,
			baseMinHeight: tokens.size.x8,
			baseFocusRingColor: tokens.color.focus,

			hoverBoxShadow: tokens.elevation.lifted,

			iconBackground: tokens.color.bg.surfaceBrand,
			iconHorizontalPadding: `${tokens.spacing.x1_5}`,
			iconVerticalPadding: `${tokens.spacing.x1_5}`,
			iconSize: `${tokens.size.x4}`,
			iconColor: `${tokens.color.content.mutedBrand}`,

			contentPadding: `${tokens.spacing.x1_5} ${tokens.spacing.x2}`,
			contentGap: tokens.spacing.x0_5,

			titleFont: tokens.text.label.largeSemiBold,
			titleFontColor: tokens.color.content.default,

			metaFont: tokens.text.label.smallRegular,
			metaFontColor: tokens.color.content.muted,

			arrowPadding: tokens.spacing.x1_5,
			arrowColor: tokens.color.content.muted,

			chunkyMinHeight: `120px`,
			chunkyIconHorizontalPadding: `${tokens.spacing.x1_5}`,
			chunkyIconVerticalPadding: `${tokens.spacing.x2}`,
			chunkyContentPadding: `${tokens.spacing.x2}`,
			chunkyContentGap: tokens.spacing.x1,
			chunkyTitleFont: tokens.text.heading.xSmall,
			chunkyMetaFont: tokens.text.label.regular,
		},
		commonInput: {
			baseBorderColor: tokens.color.border.input,
			basePaddingHorizontal: tokens.spacing.x1_5,
			basePaddingVertical: tokens.spacing.x1_5,
			baseFont: tokens.text.label.regular,
			baseTextColor: tokens.color.content.default,
			baseBorderRadius: tokens.borderRadius.small,
			baseBackground: tokens.color.bg.surface,

			focusRingColor: tokens.color.bg.interactiveMutedActive,

			hoverBorderColor: tokens.color.border.interactive,

			readOnlyBackground: tokens.color.bg.readOnly,
			readOnlyBorderColor: tokens.color.bg.readOnly,

			placeholderTextColor: tokens.color.content.placeholder,

			labelMarginBottom: tokens.spacing.x0_5,

			descriptionMarginBottom: tokens.spacing.x1,

			invalidBorderColor: tokens.color.danger.border,

			helpTextGap: tokens.spacing.x1_5,
		},
		avatar: {
			background: primitiveColorTokens.neutral[100],
			iconBackground: tokens.color.bg.surfaceBrand,
			iconColor: tokens.color.content.mutedBrand,
			fallbackBackground: tokens.color.bg.surfaceBrand,
			fallbackColor: tokens.color.content.mutedBrand,
		},
		button: {
			baseFont: tokens.text.label.largeSemiBold,
			baseBorderRadius: tokens.borderRadius.pill,
			baseFocusRingColor: tokens.color.focus,
			baseIconMarginToText: tokens.spacing.x1,

			primaryBackground: tokens.color.bg.interactive,
			primaryHoverBackground: tokens.color.bg.interactiveHover,
			primaryActiveBackground: tokens.color.bg.interactiveActive,
			primaryTextColor: tokens.color.content.onInteractive,

			secondaryBorderColor: tokens.color.bg.interactive,
			secondaryHoverBackground: tokens.color.bg.interactiveMutedHover,
			secondaryActiveBackground: tokens.color.bg.interactiveMutedActive,
			secondaryTextColor: tokens.color.content.interactive,

			ghostHoverBackground: tokens.color.bg.interactiveMutedHover,
			ghostActiveBackground: tokens.color.bg.interactiveMutedActive,
			ghostTextColor: tokens.color.content.interactive,

			dangerBackground: tokens.color.danger.bg,
			dangerHoverBackground: tokens.color.danger.bgHover,
			dangerActiveBackground: tokens.color.danger.bgActive,
			dangerTextColor: tokens.color.danger.content,

			xSmallPaddingVertical: tokens.spacing.x0_5,
			xSmallPaddingHorizontal: tokens.spacing.x2,
			xSmallFont: tokens.text.label.smallSemiBold,
			xSmallMinWidth: rem(72),
			xSmallMinHeight: rem(32),
			xSmallIconPadding: tokens.spacing.x1_5,
			xSmallIconSize: tokens.size.x2_5,

			smallPaddingVertical: tokens.spacing.x1,
			smallPaddingHorizontal: tokens.spacing.x3,
			smallFont: tokens.text.body.semiBold,
			smallMinWidth: rem(88),
			smallMinHeight: rem(40),
			smallIconPadding: tokens.spacing.x2,
			smallIconSize: tokens.size.x2_5,

			mediumPaddingVertical: tokens.spacing.x1,
			mediumPaddingHorizontal: tokens.spacing.x4,
			mediumFont: tokens.text.body.semiBold,
			mediumMinWidth: rem(96),
			mediumMinHeight: rem(48),
			mediumIconPadding: tokens.spacing.x2_5,
			mediumIconSize: tokens.size.x2_5,

			largePaddingVertical: tokens.spacing.x1_5,
			largePaddingHorizontal: tokens.spacing.x4,
			largeFont: tokens.text.label.largeSemiBold,
			largeMinWidth: rem(104),
			largeMinHeight: rem(56),
			largeIconPadding: tokens.spacing.x3,
			largeIconSize: tokens.size.x3,

			xLargePaddingVertical: tokens.spacing.x1_5,
			xLargePaddingHorizontal: tokens.spacing.x4,
			xLargeFont: tokens.text.label.largeSemiBold,
			xLargeMinWidth: rem(120),
			xLargeMinHeight: rem(64),
			xLargeIconPadding: tokens.spacing.x3,
			xLargeIconSize: tokens.size.x3,
		},
		badgeNumber: {
			interactiveBackground: tokens.color.bg.highlight,
			interactiveTextColor: tokens.color.content.onHighlight,

			brandDarkBackground: tokens.color.bg.surfaceDarkBrand,
			brandDarkTextColor: tokens.color.content.onDark,
			brandLightBackground: tokens.color.bg.surfaceBrand,
			brandLightTextColor: tokens.color.content.brand,

			neutralDarkBackground: tokens.color.bg.surfaceDark,
			neutralDarkTextColor: tokens.color.content.onDark,
			neutralLightBackground: tokens.color.bg.surface,
			neutralLightTextColor: tokens.color.content.default,

			smallText: tokens.text.label.xSmallSemiBold,

			mediumText: tokens.text.label.smallSemiBold,
		},
		badgeText: {
			// It's using x0.5 here to account for the padding of the icon.
			basePadding: `${tokens.spacing.x0_25} ${tokens.spacing.x1} ${tokens.spacing.x0_25} ${tokens.spacing.x0_5}`,
			baseBorderRadius: tokens.borderRadius.small,
			baseAdjustTextPadding: tokens.spacing.x0_5,

			interactiveBackground: tokens.color.bg.highlight,
			interactiveTextColor: tokens.color.content.onHighlight,

			brandDarkBackground: tokens.color.bg.surfaceDarkBrand,
			brandDarkTextColor: tokens.color.content.onDark,
			brandLightBackground: tokens.color.bg.surfaceBrand,
			brandLightTextColor: tokens.color.content.brand,

			neutralDarkBackground: tokens.color.bg.surfaceDark,
			neutralDarkTextColor: tokens.color.content.onDark,
			neutralLightBackground: tokens.color.bg.surface,
			neutralLightTextColor: tokens.color.content.default,

			successBackground: tokens.color.success.bg,
			successTextColor: tokens.color.success.contentMuted,

			infoBackground: tokens.color.info.bg,
			infoTextColor: tokens.color.info.contentMuted,

			warningBackground: tokens.color.warning.bg,
			warningTextColor: tokens.color.warning.contentMuted,

			dangerBackground: tokens.color.danger.bg,
			dangerTextColor: tokens.color.danger.contentMuted,

			smallText: tokens.text.label.xSmallRegular,
			smallHeight: rem(20),

			mediumText: tokens.text.label.smallRegular,
			mediumHeight: rem(24),
		},
		divider: {
			baseBackground: tokens.color.border.default,
		},
		table: {
			cardBackground: tokens.color.bg.surface,

			headerFont: tokens.text.label.smallSemiBold,
			headerSortedTextColor: tokens.color.content.interactive,
			headerBorderColor: tokens.color.border.default,
			headerSortUnderlineColor: tokens.color.border.interactive,
			headerSortIndicatorColor: tokens.color.content.placeholder,
			headerSortIndicatorActiveColor: tokens.color.content.interactive,

			bodyFont: tokens.text.label.smallRegular,
			rowBorderColor: tokens.color.border.default,
			rowHoverBackground: tokens.color.bg.interactiveMutedHover,
			rowActiveBackground: tokens.color.bg.interactiveMutedActive,
			focusRingColor: tokens.color.focus,
			emptyStateTextColor: tokens.color.content.muted,

			paginationBackground: tokens.color.bg.surfaceMuted,
		},
		pagination: {
			iconColor: tokens.color.content.interactive,
			iconDisabledColor: tokens.color.content.placeholder,
			iconHoverBackground: tokens.color.bg.interactiveMutedHover,
			iconActiveBackground: tokens.color.bg.interactiveMutedActive,
			pageFont: tokens.text.label.semiBold,
			pageHoverBackground: tokens.color.bg.interactiveMutedHover,
			pageActiveBackground: tokens.color.bg.interactiveMutedActive,
			focusRingColor: tokens.color.focus,
		},
		skeletonLoader: {
			background: tokens.color.bg.deselected,
			shimmerColor: 'rgba(255, 255, 255, 0.5)',
			borderRadius: tokens.borderRadius.small,
		},
		tableColumnMenu: {
			contentBackground: tokens.color.bg.surface,
			contentBorderColor: tokens.color.border.default,
			contentBorderRadius: tokens.borderRadius.small,
			contentBoxShadow: tokens.elevation.floating,

			itemFont: tokens.text.label.regular,
			itemFontColor: tokens.color.content.default,
			itemHoverBackground: tokens.color.bg.interactiveMutedHover,

			gripIconColor: tokens.color.content.placeholder,

			focusRingColor: tokens.color.focus,
		},
		icon: {
			xSmallSize: tokens.size.x2,

			smallSize: tokens.size.x2_5,

			mediumSize: tokens.size.x3,

			largeSize: tokens.size.x4,

			xLargeSize: tokens.size.x5,
		},
		iconButton: {
			baseText: tokens.text.label.largeSemiBold,
			baseBorderRadius: tokens.borderRadius.pill,
			baseHoverBackground: tokens.component.iconButton.hover,
			baseActiveBackground: tokens.component.iconButton.active,
			baseFocusRingColor: tokens.color.focus,

			interactiveTextColor: tokens.color.content.interactive,
			interactiveHoverBackground: tokens.component.iconButton.hover,
			interactiveActiveBackground: tokens.component.iconButton.active,

			onDarkTextColor: tokens.color.content.onDark,
			onDarkHoverBackground: tokens.component.iconButton.hoverOnDark,
			onDarkActiveBackground: tokens.component.iconButton.activeOnDark,

			infoTextColor: tokens.color.info.contentMuted,
			infoHoverBackground: tokens.component.iconButton.hoverInfo,
			infoActiveBackground: tokens.component.iconButton.activeInfo,

			successTextColor: tokens.color.success.contentMuted,
			successHoverBackground: tokens.component.iconButton.hoverSuccess,
			successActiveBackground: tokens.component.iconButton.activeSuccess,

			warningTextColor: tokens.color.warning.contentMuted,
			warningHoverBackground: tokens.component.iconButton.hoverWarning,
			warningActiveBackground: tokens.component.iconButton.activeWarning,

			dangerTextColor: tokens.color.danger.contentMuted,
			dangerHoverBackground: tokens.component.iconButton.hoverDanger,
			dangerActiveBackground: tokens.component.iconButton.activeDanger,

			smallPadding: tokens.spacing.x0_25,
			smallIconSize: tokens.size.x2_5,

			mediumPadding: tokens.spacing.x0_5,
			mediumIconSize: tokens.size.x3,

			largePadding: tokens.spacing.x0_5,
			largeIconSize: tokens.size.x4,
		},
		notificationAlert: {
			basePadding: tokens.spacing.x1,
			baseBorderRadius: tokens.borderRadius.small,
			baseText: tokens.text.label.regular,
			baseIconMargin: tokens.spacing.x1,

			infoBorderColor: tokens.color.info.borderMuted,
			infoBackground: tokens.color.info.bg,
			infoTextColor: tokens.color.info.content,
			infoIconColor: tokens.color.info.contentMuted,

			successBorderColor: tokens.color.success.borderMuted,
			successBackground: tokens.color.success.bg,
			successTextColor: tokens.color.success.content,
			successIconColor: tokens.color.success.contentMuted,

			warningBorderColor: tokens.color.warning.borderMuted,
			warningBackground: tokens.color.warning.bg,
			warningTextColor: tokens.color.warning.content,
			warningIconColor: tokens.color.warning.contentMuted,

			dangerBorderColor: tokens.color.danger.borderMuted,
			dangerBackground: tokens.color.danger.bg,
			dangerTextColor: tokens.color.danger.content,
			dangerIconColor: tokens.color.danger.contentMuted,
		},
		spinner: {
			xSmallSize: tokens.size.x2_5,

			smallSize: tokens.size.x3,

			mediumSize: tokens.size.x4,

			largeSize: tokens.size.x5,
		},
		tooltip: {
			basePadding: `${tokens.spacing.x1} ${tokens.spacing.x1_5}`,
			baseText: tokens.text.label.smallRegular,
			baseTextColor: tokens.color.content.onDark,
			baseBackground: tokens.color.bg.surfaceDark,
			baseElevation: tokens.elevation.lifted,
			baseBorderRadius: tokens.borderRadius.small,
			baseArrowMargin: tokens.spacing.x1,

			darkBackground: tokens.color.bg.surfaceDark,
			darkTextColor: tokens.color.content.onDark,
			lightBackground: tokens.color.bg.surface,
			lightTextColor: tokens.color.content.default,
			interactiveBackground: tokens.color.bg.interactive,
			interactiveTextColor: tokens.color.content.onInteractive,

			arrowSize: tokens.size.x1_5,
			arrowBorderRadius: tokens.borderRadius.xSmall,
		},
		input: {
			baseBorderColor: tokens.color.border.input,
			basePaddingHorizontal: tokens.spacing.x1_5,
			basePaddingVertical: tokens.spacing.x1_5,
			baseFont: tokens.text.label.regular,
			baseTextColor: tokens.color.content.default,
			baseBorderRadius: tokens.borderRadius.small,
			baseBackground: tokens.color.bg.surface,
			baseMinHeight: rem(48),

			focusRingColor: tokens.color.bg.interactiveMutedActive,

			hoverBorderColor: tokens.color.border.interactive,

			readOnlyBackground: tokens.color.bg.readOnly,
			readOnlyBorderColor: tokens.color.bg.readOnly,

			placeholderTextColor: tokens.color.content.placeholder,

			invalidBorderColor: tokens.color.danger.border,

			iconPaddingLeft: tokens.spacing.x6,
			iconColor: tokens.color.content.muted,
			iconSize: tokens.size.x3,
			iconFocusRingColor: tokens.color.focus,
		},
		textArea: {
			baseBorderColor: tokens.color.border.input,
			basePaddingHorizontal: tokens.spacing.x1_5,
			basePaddingVertical: tokens.spacing.x1_5,
			baseFont: tokens.text.label.regular,
			baseTextColor: tokens.color.content.default,
			baseBorderRadius: tokens.borderRadius.small,
			baseBackground: tokens.color.bg.surface,
			baseMinHeight: rem(48),

			focusRingColor: tokens.color.bg.interactiveMutedActive,

			hoverBorderColor: tokens.color.border.interactive,

			readOnlyBackground: tokens.color.bg.readOnly,
			readOnlyBorderColor: tokens.color.bg.readOnly,

			placeholderTextColor: tokens.color.content.placeholder,

			highlightBackground: tokens.color.warning.bgLight,
			highlightBorderColor: tokens.color.warning.border,
			highlightHoverBorderColor: tokens.color.warning.border,
			highlightPlaceholderTextColor: tokens.color.warning.contentPlaceholder,

			invalidBorderColor: tokens.color.danger.border,
			invalidPaddingRight: tokens.spacing.x5,
			invalidIconColor: tokens.color.danger.contentMuted,
			invalidIconSize: tokens.size.x3,
		},
		label: {
			baseFont: tokens.text.label.semiBold,
			baseTextColor: tokens.color.content.default,
			optionalFont: tokens.text.label.xSmallRegular,
			optionalTextColor: tokens.color.content.muted,
		},
		inputDate: {
			iconPaddingLeft: tokens.spacing.x6,
			iconColor: tokens.color.content.muted,
			iconSize: tokens.size.x3,
		},
		inputTime: {
			iconPaddingLeft: tokens.spacing.x6,
			iconColor: tokens.color.content.muted,
			iconSize: tokens.size.x3,
		},
		inputPhoneNumber: {
			countrySelectTriggerHoverBg: tokens.color.bg.interactiveMutedHover,
			countrySelectTriggerActiveBg: tokens.color.bg.interactiveMutedActive,
		},
		dialogAlert: {
			background: tokens.color.bg.surface,
			borderRadius: tokens.borderRadius.large,
			boxShadow: tokens.elevation.flying,
			padding: tokens.spacing.x1,
			zIndex: '2',
			maxWidth: '550px',

			innerPadding: tokens.spacing.x2,

			headerText: tokens.text.heading.xSmall,
			headerTextAtMediumAndAbove: tokens.text.heading.small,
			headerMarginBottom: tokens.spacing.x1_5,

			bodyText: tokens.text.body.regular,
			bodyTextAtMediumAndAbove: tokens.text.body.largeRegular,
			bodyMarginBottom: tokens.spacing.x3,

			errorMarginBottom: tokens.spacing.x2_5,

			buttonGroupGap: tokens.spacing.x1,
		},
		dialogModal: {
			background: tokens.color.bg.surface,
			borderRadius: tokens.borderRadius.large,
			boxShadow: tokens.elevation.flying,
			padding: tokens.spacing.x2_5,
			zIndex: '2',
			gap: tokens.spacing.x2_5,

			titleFont: tokens.text.heading.xSmall,
			titleFontAtMediumAndAbove: tokens.text.heading.small,

			descriptionFont: tokens.text.body.regular,
			descriptionFontAtMediumAndAbove: tokens.text.body.largeRegular,

			contentFont: tokens.text.body.regular,
		},
		tabNavigation: {
			triggerPadding: `${tokens.spacing.x1} ${tokens.spacing.x1_5}`,
			triggerFont: tokens.text.label.largeRegular,
			triggerPassiveColor: tokens.color.content.muted,
			triggerActiveFont: tokens.text.label.largeSemiBold,
			triggerActiveColor: tokens.color.content.default,
			triggerActiveBackground: tokens.color.content.interactive,
			triggerHoverColor: tokens.color.content.interactive,
			sliderBackground: tokens.color.border.default,
		},
		inputSelect: {
			triggerColor: tokens.color.content.default,
			triggerBackground: tokens.color.bg.surface,
			triggerPlaceholderColor: tokens.color.content.muted,
			triggerSelectedBackground: tokens.color.bg.interactiveMutedActive,
			triggerInvalidBackground: tokens.color.bg.surface,
			triggerInvalidBorder: tokens.color.danger.border,
			triggerIconColor: tokens.color.content.muted,
			triggerIconSize: tokens.size.x2_5,
			triggerIconGap: tokens.spacing.x1,
			selectIconColor: tokens.color.content.muted,
			contentBackground: tokens.color.bg.surface,
			contentBorderRadius: tokens.borderRadius.small,
			contentBorderColor: tokens.color.border.default,
			contentBoxShadow: tokens.elevation.floating,
			itemColor: tokens.color.content.default,
			itemBorderRadius: tokens.borderRadius.xSmall,
			itemHeight: tokens.size.x5,
			itemHeightMobile: tokens.size.x7,
			itemPadding: `${tokens.spacing.x1} ${tokens.spacing.x4} ${tokens.spacing.x1} ${tokens.spacing.x1}`,
			itemPaddingMobile: `${tokens.spacing.x1} ${tokens.spacing.x5} ${tokens.spacing.x1} ${tokens.spacing.x2}`,
			itemDisabledColor: tokens.color.content.muted,
			itemDisabledBackground: tokens.color.bg.readOnly,
			itemCheckedFont: tokens.text.label.semiBold,
			itemCheckedBackground: tokens.color.bg.interactiveMutedActive,
			itemHighlightBackground: tokens.color.bg.interactiveMutedHover,
			itemSeparatorBackground: tokens.color.border.default,
			itemSeparatorSpacing: tokens.spacing.x0_5,
			itemIndicatorSpacing: tokens.spacing.x1,
			itemIndicatorSpacingMobile: tokens.spacing.x2,
			itemIndicatorColor: tokens.color.content.interactive,
			itemScrollButtonColor: tokens.color.content.muted,
			itemViewportPadding: tokens.spacing.x0_5,
			itemViewportPaddingMobile: `0 0 ${tokens.spacing.x1_5}`,
		},
		inputRadio: {
			focusColor: tokens.color.focus,
			labelMarginBottom: tokens.spacing.x0_5,

			itemsGap: tokens.spacing.x1,
			itemsMargin: tokens.spacing.x0_5,
			itemsHorizontalGap: `${tokens.spacing.x1} ${tokens.spacing.x2}`,
			outlinedItemsGap: tokens.spacing.x0_5,

			itemFont: tokens.text.label.regular,
			itemTextColor: tokens.color.content.default,
			itemGap: tokens.spacing.x1,

			indicatorBackground: tokens.color.bg.surface,
			indicatorBorderColor: tokens.color.border.input,
			indicatorOuterSize: tokens.size.x3,
			indicatorInnerSize: tokens.size.x1_5,

			activeIndicatorOutlineColor: tokens.color.bg.interactiveMutedActive,

			checkedItemFont: tokens.text.label.semiBold,
			checkedIndicatorBorderColor: tokens.color.border.interactive,

			outlinedItemPadding: tokens.spacing.x1_5,
			outlinedItemMinHeight: tokens.size.x6,
			outlinedItemBorderColor: tokens.color.border.input,
			outlinedItemBorderRadius: tokens.borderRadius.medium,

			hoverOutlinedItemBorderColor: tokens.color.border.interactive,

			activeOutlinedItemOutlineColor: tokens.color.bg.interactiveMutedActive,

			checkedOutlinedItemBackground: tokens.color.bg.interactiveMutedActive,

			disabledOutlinedItemBackground: tokens.color.bg.readOnly,
			disabledIndicatorBorderColor: tokens.color.border.input,
			disabledIndicatorBackground: tokens.color.bg.readOnly,
		},
		inputCheckbox: {
			focusColor: tokens.color.focus,
			labelMarginBottom: tokens.spacing.x0_5,

			itemsGap: tokens.spacing.x1,
			itemsMargin: tokens.spacing.x0_5,
			itemsHorizontalGap: `${tokens.spacing.x1} ${tokens.spacing.x2}`,
			outlinedItemsGap: tokens.spacing.x0_5,

			itemFont: tokens.text.label.regular,
			itemTextColor: tokens.color.content.default,
			itemGap: tokens.spacing.x1,

			subLabelFont: tokens.text.label.xSmallRegular,

			indicatorBackground: tokens.color.bg.surface,
			indicatorBorderColor: tokens.color.border.input,
			indicatorSize: tokens.size.x2_5,

			activeIndicatorOutlineColor: tokens.color.bg.interactiveMutedActive,

			checkedItemFont: tokens.text.label.semiBold,
			checkedIndicatorBorderColor: tokens.color.bg.interactive,
			checkedIndicatorBackground: tokens.color.bg.interactive,

			outlinedItemPadding: tokens.spacing.x1_5,
			outlinedItemMinHeight: tokens.size.x6,
			outlinedItemBorderColor: tokens.color.border.input,
			outlinedItemBorderRadius: tokens.borderRadius.medium,

			hoverOutlinedItemBorderColor: tokens.color.border.interactive,

			activeOutlinedItemOutlineColor: tokens.color.bg.interactiveMutedActive,

			checkedOutlinedItemBackground: tokens.color.bg.interactiveMutedActive,

			disabledOutlinedItemBackground: tokens.color.bg.readOnly,
			disabledIndicatorBorderColor: tokens.color.border.input,
			disabledIndicatorBackground: tokens.color.bg.readOnly,
		},
		inputSwitch: {
			labelColor: tokens.color.content.default,
			labelFont: tokens.text.label.regular,
			labelCheckedFont: tokens.text.label.semiBold,
			inputIconColor: tokens.color.bg.surface,
			inputBg: tokens.color.bg.deselected,
			inputCheckedBg: tokens.color.bg.interactive,
			inputCheckedHoverBg: tokens.color.bg.interactiveHover,
			inputCheckedActiveBg: tokens.color.bg.interactiveActive,
			inputHoverBg: tokens.color.bg.deselectedHover,
			inputActiveBg: tokens.color.bg.deselectedActive,
			inputFocus: tokens.color.focus,
			thumbBg: tokens.color.bg.surface,
		},
		inputCombobox: {
			triggerFontColor: tokens.color.content.default,
			triggerIconColor: tokens.color.content.muted,
			triggerSelectedFont: tokens.text.label.semiBold,
			triggerSelectedBackground: tokens.color.bg.interactiveMutedActive,

			contentBackground: tokens.color.bg.surface,
			contentBorderRadius: tokens.borderRadius.small,
			contentBorderColor: tokens.color.border.default,
			contentBoxShadow: tokens.elevation.floating,
			contentFontColor: tokens.color.content.default,

			inputIconColor: tokens.color.content.muted,
			inputFont: tokens.text.body.regular,
			inputBorderColor: tokens.color.border.default,
			inputHeight: `${tokens.size.x3}`,

			emptyFont: tokens.text.label.regular,
			emptyFontColor: tokens.color.content.default,
			emptyPadding: `${tokens.spacing.x1}`,

			itemFont: tokens.text.label.regular,
			itemFontColor: tokens.color.content.default,
			itemBorderRadius: tokens.borderRadius.xSmall,
			itemMinHeight: tokens.size.x6,
			itemPadding: `${tokens.spacing.x0_5} ${tokens.spacing.x1}`,
			itemFocusColor: tokens.color.focus,
			itemHoverBackground: tokens.color.bg.interactiveMutedHover,
			itemSelectedFont: tokens.text.label.semiBold,
			itemSelectedBackground: tokens.color.bg.interactiveMutedActive,

			itemSubLabelFont: tokens.text.label.xSmallRegular,
			itemSubLabelFontColor: tokens.color.content.muted,

			itemStatusOnlineColor: tokens.color.success.border,
			itemStatusOfflineColor: tokens.color.content.placeholder,
			itemStatusRingColor: tokens.color.bg.surface,

			actionsBackground: tokens.color.bg.surfaceMuted,
			actionsPadding: tokens.spacing.x0_5,
			actionsGap: tokens.spacing.x0_5,
		},
		textLink: {
			baseText: tokens.text.label.semiBold,
			baseTextColor: tokens.color.content.interactive,
			baseFocusRingColor: tokens.color.focus,
			baseIconSize: tokens.size.x2,
			baseIconMargin: tokens.spacing.x1,
			activeTextColor: tokens.color.content.default,
			smallText: tokens.text.label.smallSemiBold,
			smallIconSize: tokens.size.x2,
			smallIconMargin: tokens.size.x0_5,
			mediumText: tokens.text.label.semiBold,
			mediumIconSize: tokens.size.x2_5,
			mediumIconMargin: tokens.size.x1,
			largeText: tokens.text.label.largeSemiBold,
			largeIconSize: tokens.size.x3,
			largeIconMargin: tokens.size.x1,
		},
		helpText: {
			baseFont: tokens.text.label.xSmallRegular,
			baseTextColor: tokens.color.content.muted,
			baseMarginTop: tokens.spacing.x0_5,
			baseMarginLeft: tokens.spacing.x0_25,

			invalidTextColor: tokens.color.danger.contentMuted,
		},
		description: {
			baseFont: tokens.text.label.smallRegular,
			baseTextColor: tokens.color.content.muted,
		},
		characterCount: {
			baseText: tokens.text.label.xSmallRegular,
			baseTextColor: tokens.color.content.muted,
			baseMarginTop: tokens.spacing.x0_5,

			invalidTextColor: tokens.color.danger.contentMuted,
		},
		chip: {
			baseBorderRadius: tokens.borderRadius.pill,
			baseGap: tokens.spacing.x0_5,
			baseRemoveGap: tokens.spacing.x0_25,
			basePaddingInline: tokens.spacing.x1,
			baseRemovePaddingInlineEnd: '0',

			outlinedBorderColor: tokens.color.border.default,
			outlinedTextColor: tokens.color.content.default,
			outlinedIconColor: tokens.color.content.interactive,
			outlinedRemoveIconColor: tokens.color.content.muted,

			defaultBackground: tokens.color.bg.interactive,
			defaultTextColor: tokens.color.content.onInteractive,

			successBackground: tokens.color.success.bg,
			successTextColor: tokens.color.success.contentMuted,

			infoBackground: tokens.color.info.bg,
			infoTextColor: tokens.color.info.contentMuted,

			warningBackground: tokens.color.warning.bg,
			warningTextColor: tokens.color.warning.contentMuted,

			dangerBackground: tokens.color.danger.bg,
			dangerTextColor: tokens.color.danger.contentMuted,

			brandDarkBackground: primitiveColorTokens.primary[900],
			brandDarkTextColor: tokens.color.content.onDark,
			brandLightBackground: primitiveColorTokens.primary[300],
			brandLightTextColor: primitiveColorTokens.primary[900],

			neutralDarkBackground: tokens.color.bg.surfaceDark,
			neutralDarkTextColor: tokens.color.content.onDark,
			neutralLightBackground: primitiveColorTokens.neutral[300],
			neutralLightTextColor: primitiveColorTokens.neutral[900],

			smallText: tokens.text.label.xSmallRegular,
			smallHeight: rem(20),

			mediumText: tokens.text.label.smallRegular,
			mediumHeight: rem(24),
		},
		colorSwatch: {
			baseSize: rem(10),
			baseBorderRadius: rem(2),
		},
		fileViewer: {
			background: '#000',
			textColor: tokens.color.content.onDark,
		},
		dropdownMenu: {
			contentBackground: tokens.color.bg.surface,
			contentBorderRadius: tokens.borderRadius.small,
			contentBorderColor: tokens.color.border.default,
			contentBoxShadow: tokens.elevation.floating,

			itemFont: tokens.text.label.regular,
			itemFontColor: tokens.color.content.default,
			itemBorderRadius: tokens.borderRadius.xSmall,
			itemMinHeight: tokens.size.x5,
			itemPadding: `${tokens.spacing.x1}`,
			itemHoverBackground: tokens.color.bg.interactiveMutedHover,
			itemActiveBackground: tokens.color.bg.interactiveMutedActive,
			itemDisabledBackground: tokens.color.bg.readOnly,
		},
		attachment: {
			baseBackground: tokens.color.bg.surface,
			baseBorderColor: tokens.color.border.default,
			baseBorderRadius: tokens.borderRadius.small,
			baseBoxShadow: tokens.elevation.skim,
			basePadding: tokens.spacing.x1,

			hoverBoxShadow: tokens.elevation.lifted,

			previewBackground: tokens.color.bg.surfaceBrand,
			previewIconColor: tokens.color.content.mutedBrand,

			nameFont: tokens.text.label.smallSemiBold,
			nameFontColor: tokens.color.content.default,

			metaFont: tokens.text.label.smallRegular,
			metaFontColor: tokens.color.content.muted,
		},
		toastNotification: {
			baseBorderRadius: tokens.borderRadius.medium,
			basePadding: tokens.size.x2,
			baseGap: tokens.size.x2,
			baseElevation: tokens.elevation.floating,

			successIconColor: tokens.color.success.content,
			successTextColor: tokens.color.success.content,
			successBackground: tokens.color.success.bg,
			successBorderColor: tokens.color.success.border,

			dangerIconColor: tokens.color.danger.content,
			dangerTextColor: tokens.color.danger.content,
			dangerBackground: tokens.color.danger.bg,
			dangerBorderColor: tokens.color.danger.border,

			warningIconColor: tokens.color.warning.content,
			warningTextColor: tokens.color.warning.content,
			warningBackground: tokens.color.warning.bg,
			warningBorderColor: tokens.color.warning.border,

			infoIconColor: tokens.color.info.content,
			infoTextColor: tokens.color.info.content,
			infoBackground: tokens.color.info.bg,
			infoBorderColor: tokens.color.info.border,
		},
		inputRating: {
			focusColor: tokens.color.focus,
			labelMarginBottom: tokens.spacing.x0_5,

			itemsGap: tokens.spacing.x0_5,
			itemsMargin: tokens.spacing.x0_5,
			itemsHorizontalGap: `${tokens.spacing.x1} ${tokens.spacing.x2}`,
			outlinedItemsGap: tokens.spacing.x0_5,

			itemFont: tokens.text.label.regular,
			itemTextColor: tokens.color.content.default,
			itemGap: tokens.spacing.x1,

			indicatorBackground: tokens.color.bg.surface,
			indicatorBorderColor: tokens.color.border.input,
			indicatorHover: tokens.color.bg.interactiveMutedActive,
			indicatorOuterSize: tokens.size.x5,
			indicatorInnerSize: tokens.size.x1_5,

			activeIndicatorOutlineColor: tokens.color.bg.interactiveMutedActive,

			checkedItemFont: tokens.text.label.semiBold,
			checkedIndicatorBorderColor: tokens.color.border.interactive,

			outlinedItemPadding: tokens.spacing.x1_5,
			outlinedItemMinHeight: tokens.size.x6,
			outlinedItemBorderColor: tokens.color.border.input,
			outlinedItemBorderRadius: tokens.borderRadius.medium,

			hoverOutlinedItemBorderColor: tokens.color.border.interactive,

			activeOutlinedItemOutlineColor: tokens.color.bg.interactiveMutedActive,

			checkedOutlinedItemBackground: tokens.color.bg.interactiveMutedActive,

			disabledOutlinedItemBackground: tokens.color.bg.readOnly,
			disabledIndicatorBorderColor: tokens.color.border.input,
			disabledIndicatorBackground: tokens.color.bg.readOnly,
		},
	},
};
